import type { BlobLike, ImageArray } from "@pdfking/compatibility"
import type { Pixel } from "@pdfking/renderer-api"

const toNativeBlob = async (blob: BlobLike): Promise<Blob> => {
  if (blob instanceof Blob) return blob
  return new Blob([await blob.arrayBuffer()], { type: blob.type })
}

const importWithImageBitmap = async (blob: Blob): Promise<ImageArray> => {
  const bitmap = await createImageBitmap(blob)
  const { width, height } = bitmap

  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Failed to get canvas 2d context")

  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const imageData = ctx.getImageData(0, 0, width, height)
  return {
    width: width as Pixel,
    height: height as Pixel,
    data: new Uint8Array(imageData.data.buffer),
  }
}

const importWithImage = (blob: Blob): Promise<ImageArray> =>
  Promise.resolve(URL.createObjectURL(blob)).then(
    url =>
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(url)
          const canvas = document.createElement("canvas")
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext("2d")!
          ctx.drawImage(img, 0, 0)
          const imageData = ctx.getImageData(0, 0, img.width, img.height)
          resolve({
            width: img.width as Pixel,
            height: img.height as Pixel,
            data: new Uint8Array(imageData.data.buffer),
          })
        }
        img.onerror = e => {
          URL.revokeObjectURL(url)
          reject(e)
        }
        img.src = url
      })
  )

export const ImportImage = async (blob: BlobLike): Promise<ImageArray> => {
  const nativeBlob = await toNativeBlob(blob)

  if (typeof OffscreenCanvas !== "undefined") {
    return importWithImageBitmap(nativeBlob)
  }

  if (typeof Image !== "undefined") {
    return importWithImage(nativeBlob)
  }

  throw new Error("No image import implementation available")
}
